<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { SHORTPROFILE_FOR_361, SHORTPROFILE_FOR_361_ITALIAN } from "../../utils/ProjectUtils";

const { t } = useI18n();

interface Role {
    id: number;
    type: string;
    name: string;
    selected: boolean;
    toggleable: boolean;
    editMode: boolean;
}

const props = defineProps({
    role: {
        type: Object as () => Role,
        required: true,
    },
    projectId: {
        type: Number,
        required: true,
    },
    projectProduct: {
        type: String,
        required: true,
    },
    isEditingNames: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits<{
    toggle: [role: Role];
    updateName: [roleType: string, newName: string];
}>();

const textMaxLength = 15;

/**
 * Handle role click: toggle role and unfocus the element
 */
async function handleRoleClick(event: Event) {
    // Only allow toggling if the role is toggleable and we're not editing names
    if (props.role.toggleable && !props.isEditingNames) {
        emit('toggle', props.role);
        // Unfocus the clicked element
        if (event.target) {
            (event.target as HTMLElement).blur();
        }
    }
}

/**
 * Handle keyboard events for role selection (Enter and Space)
 */
async function handleRoleKeydown(event: KeyboardEvent) {
    // Only handle Enter and Space keys, and only when not in edit mode
    if (!props.isEditingNames && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        emit('toggle', props.role);
        // Unfocus after keyboard interaction
        if (event.target) {
            (event.target as HTMLElement).blur();
        }
    }
}

/**
 * Handle name changes
 */
function handleNameUpdate(event: Event) {
    const target = event.target as HTMLInputElement;
    emit('updateName', props.role.type, target.value);
}

/**
 * Check if we should show labels for this product
 */
const shouldShowLabel = () => {
    return props.projectProduct === SHORTPROFILE_FOR_361 || 
           props.projectProduct === SHORTPROFILE_FOR_361_ITALIAN;
};

/**
 * Truncated role name limited to 15 characters
 */
const truncatedRoleName = computed(() => {
    const name = props.role.name;
    return name.length > textMaxLength ? name.substring(0, textMaxLength).trim() + '...' : name;
});

/**
 * Dynamic width that provides consistent sizing for all role items
 * regardless of toggleable state
 */
const dynamicWidth = computed(() => {
    // Base width for consistency across all role types
    const baseWidth = 5; // minimum width in rem
    const maxWidth = 12; // maximum width in rem
    
    // Calculate width based on the role name length
    const charWidth = 0.5; // approximate width per character in rem
    const nameLength = props.role.name.length;
    const calculatedWidth = Math.max(baseWidth, Math.min(maxWidth, nameLength * charWidth + 2));
    
    return `${calculatedWidth}rem`;
});

/**
 * Get appropriate tooltip text based on role state
 */
const getTooltipText = () => {
    if (props.isEditingNames) {
        return ''; // No tooltip when editing
    }
    if (!props.role.toggleable) {
        return t('project-configuration.roles.chipDisabledTooltip');
    }
    if (props.role.name.length > 15) {
        return props.role.name; // Show full name if truncated
    }
    return t('project-configuration.roles.toggleRole', { role: props.role.name });
};
</script>

<template>
    <div class="role-item-container">
        <label
            :for="`role-item_${role.type.toLowerCase()}-${projectId}`"
            v-if="shouldShowLabel()"
            class="row flex justify-start text-2xs mr-2"
        >
            {{ t(`project-configuration.roles.${role.type.toLowerCase()}`) }}
        </label>
        
        <div class="row">
            <InputText
                :id="`role-item_${role.type.toLowerCase()}-${projectId}`"
                :model-value="isEditingNames ? role.name : truncatedRoleName"
                :readonly="!isEditingNames"
                :tabindex="0"
                :maxlength="isEditingNames ? 15 : undefined"
                class="role-chip"
                :class="{ 'role-chip-selected': role.selected, 'role-chip-unselected': !role.selected, 'role-chip-non-toggleable': !role.toggleable, }"
                :style="{ width: dynamicWidth }"
                @click="handleRoleClick($event)"
                @keydown="handleRoleKeydown($event)"
                @input="handleNameUpdate($event)"
                v-tooltip.top="getTooltipText()"
            />
        </div>
    </div>
</template>

<style scoped>
/* Unified chip-style input for all roles */
.role-chip {
    text-align: center;
    font-size: 14px !important;
    margin-right: 0.5rem;
    border: none !important;
    outline: none !important;
    transition: background-color 0.3s ease, width 0.2s ease;
    padding-left: 0;
    padding-right: 0;
}

/* Readonly mode (display/chip mode) */
.role-chip[readonly] {
    cursor: pointer !important;
    caret-color: transparent !important;
}

/* Non-toggleable roles have different cursor */
.role-chip[readonly].role-chip-non-toggleable {
    cursor: default !important;
}

/* Focus styles for keyboard navigation */
.role-chip[readonly]:focus {
    outline: 2px solid var(--p-secondary-500) !important;
    outline-offset: 2px !important;
}

.role-chip[readonly].role-chip-selected {
    background-color: var(--p-secondary-500) !important;
    color: white !important;
    outline: 1px solid var(--p-secondary-500) !important;
}

.role-chip[readonly].role-chip-unselected {
    background-color: var(--p-secondary-100) !important;
    color: white !important;
}

/* High contrast mode: unselected roles should have black text */
:global(.theme-high-contrast) .role-chip[readonly].role-chip-unselected {
    color: black !important;
    border: 1px solid var(--p-secondary-500) !important;
}

/* Even more specific selector for debugging */
:global(.theme-high-contrast) .role-item-container .role-chip[readonly].role-chip-unselected {
    color: black !important;
    background-color: white !important;
    border: 2px solid black !important;
}

.role-chip[readonly]:hover.role-chip-selected:not(.role-chip-non-toggleable) {
    background-color: var(--p-secondary-900) !important;
    transform: scale(1.1);
    transition: transform 0.1s ease;
}

.role-chip[readonly]:hover.role-chip-unselected:not(.role-chip-non-toggleable) {
    background-color: var(--p-secondary-300) !important;
    transform: scale(1.1);
    transition: transform 0.1s ease;
}

/* Disabled state */
.role-chip-disabled {
    opacity: 0.9 !important;
    cursor: default !important;
}

/* Edit mode (when not readonly) */
.role-chip:not([readonly]) {
    background-color: #ffffff !important;
    color: #000000 !important;
    outline: 1px solid var(--p-secondary-500) !important;
    cursor: text !important;
}

.role-chip:not([readonly]):focus {
    box-shadow: 0 0 0 2px rgba(14, 55, 114, 0.3) !important;
    border-color: var(--p-secondary-900) !important;
}

/* Layout helpers */
.role-item-container {
    margin-right: 0.5rem;
}

.text-2xs {
    font-size: 9px;
}
</style>

<!-- Global styles for high contrast mode (not scoped) -->
<style>
.theme-high-contrast .role-chip[readonly].role-chip-unselected {
    color: black !important;
    background-color: white !important;
    border: 2px solid black !important;
}
</style>
